import { createContext, useContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_LOCALE,
  getLocale,
  isEnabledLocale,
  localizedPath,
  parsePath,
  type LocaleDef,
} from "@/lib/i18n/locales";
import { I18nextProvider } from "react-i18next";
import { loadAndSetLocale, i18n, i18nFor } from "@/lib/i18n";

interface LanguageContextValue {
  /** Active locale code (matches the URL prefix; "en" at root). */
  locale: string;
  localeDef: LocaleDef;
  /** Switch language, persist the choice, and navigate to the same page. */
  setLocale: (code: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = "statlign_locale";
const SYNC_KEY = "statlign_locale_synced";

/**
 * Locale the visitor explicitly picked on a previous visit, captured once at
 * module load — before any effect overwrites it with the URL-derived locale.
 */
const SAVED_LOCALE: string | null = (() => {
  try {
    return typeof window === "undefined" ? null : localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
})();

/**
 * Drives the presentation-layer language.
 *
 * The active locale is derived from the URL prefix (the router mounts under a
 * per-locale basename), so this provider's job is to (a) keep i18next in sync
 * with the URL, (b) reflect the locale onto <html lang>/<dir>, and (c) persist
 * the user's explicit choice to localStorage + their profile so it wins next
 * visit (priority: profile/localStorage → URL → browser → English).
 */
export function LanguageProvider({
  locale,
  children,
}: {
  locale: string;
  children: ReactNode;
}) {
  const [ready, setReady] = useState(i18n.language === locale);
  const navigate = useNavigate();
  const location = useLocation();

  // Load the dictionary + switch i18next whenever the URL locale changes.
  useEffect(() => {
    let active = true;
    loadAndSetLocale(locale).finally(() => active && setReady(true));
    return () => {
      active = false;
    };
  }, [locale]);

  // Reflect locale onto the document for a11y, RTL and crawlers.
  useEffect(() => {
    const def = getLocale(locale);
    document.documentElement.lang = def.code;
    document.documentElement.dir = def.dir;
  }, [locale]);

  // Persist the URL-derived locale so it is remembered on the next visit.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore private-mode storage errors */
    }
  }, [locale]);

  // Sync a previously-saved preference onto the URL. If the visitor lands on an
  // un-prefixed (English) URL but has explicitly chosen another language before,
  // redirect once per session to the localized equivalent of the same page so
  // language selection stays consistent while navigating across pages.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = SAVED_LOCALE;
    let alreadySynced = false;
    try {
      alreadySynced = sessionStorage.getItem(SYNC_KEY) === "1";
    } catch {
      return;
    }
    if (alreadySynced || !saved || saved === locale) return;
    if (!isEnabledLocale(saved)) return;

    // Only act when the URL carries no explicit locale prefix — an explicit
    // prefix in the URL always wins over the stored preference.
    const { locale: urlLocale, path } = parsePath(window.location.pathname);
    if (urlLocale !== DEFAULT_LOCALE || locale !== DEFAULT_LOCALE) return;
    try {
      sessionStorage.setItem(SYNC_KEY, "1");
    } catch {
      /* ignore */
    }
    window.location.replace(
      localizedPath(saved, path) + window.location.search + window.location.hash,
    );
  }, [locale]);


  const setLocale = useCallback(
    (code: string) => {
      const target = isEnabledLocale(code) ? code : DEFAULT_LOCALE;
      try {
        localStorage.setItem(STORAGE_KEY, target);
      } catch {
        /* ignore */
      }
      // Best-effort persist to the signed-in user's profile.
      void supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          void supabase
            .from("profiles")
            .update({ preferred_language: target })
            .eq("id", data.user.id);
        }
      });
      // Keep the current page; only the locale prefix changes. A full document
      // navigation guarantees the router remounts under the new basename and
      // the correct prerendered/translated HTML is fetched for crawlers.
      const { path } = parsePath(location.pathname);
      const dest = localizedPath(target, path) + location.search + location.hash;
      if (target === locale) return;
      window.location.assign(dest);
    },
    [locale, location.pathname, location.search, location.hash],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ locale, localeDef: getLocale(locale), setLocale }),
    [locale, setLocale],
  );

  // The root route's beforeLoad has already registered this locale's
  // dictionary (server and client), so render immediately — returning null
  // here used to ship translated pages to crawlers with an empty body.
  void ready;
  const instance = useMemo(() => i18nFor(locale), [locale]);

  return (
    <I18nextProvider i18n={instance}>
      <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
    </I18nextProvider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  // Fall back to a URL-derived, read-only value instead of throwing. This keeps
  // the app rendering if a consumer mounts before/outside the provider (e.g. a
  // stale module instance during HMR) rather than blanking the whole page.
  if (!ctx) {
    const fallbackLocale = parsePath(
      typeof window !== "undefined" ? window.location.pathname : "/",
    ).locale;
    return {
      locale: fallbackLocale,
      localeDef: getLocale(fallbackLocale),
      setLocale: (code: string) => {
        const target = isEnabledLocale(code) ? code : DEFAULT_LOCALE;
        if (typeof window === "undefined") return;
        const { path } = parsePath(window.location.pathname);
        window.location.assign(
          localizedPath(target, path) + window.location.search + window.location.hash,
        );
      },
    };
  }
  return ctx;
}

